import axios from 'axios';
import { API_URL } from '@/config/config';

// Make sure the API URL doesn't end with /api to prevent double paths
const baseURL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// API service configured for authentication with JWT tokens

// Create an axios instance with default config
const api = axios.create({
  baseURL: baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to check if a URL is an invitation endpoint that should work without authentication
const isInvitationEndpoint = (url?: string): boolean => {
  if (!url) return false;

  // Organization invitation endpoints
  if (url.includes('/org-invitations/')) return true;

  // Team membership invitation endpoints
  if (url.includes('/memberships/') && (url.includes('/check/') || url.includes('/accept/')))
    return true;

  return false;
};

// Add a request interceptor to attach auth token
api.interceptors.request.use(
  config => {
    // Skip token attachment for invitation endpoints (they should work without authentication)
    const isInvitationUrl = isInvitationEndpoint(config.url);

    // Attach authorization token to requests (except invitation endpoints)
    const token = localStorage.getItem('access_token');
    if (token && !isInvitationUrl) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add a response interceptor to handle token refresh
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Skip automatic login redirect for invitation endpoints (they should handle their own errors)
    const isInvitationUrl = isInvitationEndpoint(originalRequest.url);

    // If error is 401 and we haven't tried to refresh the token yet (and not an invitation endpoint)
    if (error.response?.status === 401 && !originalRequest._retry && !isInvitationUrl) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          // If no refresh token, logout the user
          localStorage.removeItem('access_token');
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Try to get a new token
        const response = await axios.post(`${baseURL}/token/refresh/`, {
          refresh: refreshToken,
        });

        if (response.data.access) {
          localStorage.setItem('access_token', response.data.access);

          // Update the original request authorization header
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;

          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If refresh failed, logout the user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
